import InstitutionLogo from '@/components/cashflow/InstitutionLogo';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { showConfirmModal } from '@/components/common/modals/ConfirmModal';
import { useToasts } from '@/components/common/Toasts';
import UnknownBalanceDateInfoButton from '@/components/financialEntities/UnknownBalanceDateInfoButton';
import usePlaid from '@/components/plaid/usePlaid';
import { useTracking } from '@/services/analytics/useTracking';
import { logError } from '@/util/logger';
import { useMutation } from '@apollo/client';
import {
    ExternalFinancialEntity,
    getFEBaseConfig,
    getName,
    getOwnershipTypeOptionValues,
} from '@common/financialEntities';
import { FieldConfig } from '@common/financialEntities/config';
import {
    InvertTransactionEntryAmountsDocument,
    LinkStatus,
} from '@common/graphql/generated';
import {
    canLink,
    configByFECombinedType,
    getFECombinedType,
    getFECombinedTypeOptions,
    getLastUpdatedBalanceDate,
    getTransactionsLastUpdatedAt,
    isLatestBalanceAccurate,
} from '@common/react/financialEntities';
import { useCurrentUser } from '@common/react/user';
import { TOASTS } from '@common/toasts';
import React, { FC } from 'react';
import ButtonWithIcon from '../common/ButtonWithIcon';
import { EntityViewGridSection } from '../common/EntityView';
import { Link03, LinkBroken01 } from '../common/icons';
import InputField from '../common/InputField';
import { Paragraph, Text } from '../common/Typography';
import { showAccountLinkingConfirmationModal } from './AccountLinkingConfirmationModal';
import { showFinancialEntityJointToIndividualModal } from './FinancialEntityJointToIndividualModal';
import { showFinancialEntityTypeChangeModal } from './FinancialEntityTypeChangeModal';
import { formatValue, getConfig } from './helpers';
import { OwnershipType } from './OwnershipType';

export interface FinancialEntityDetailInfoSectionProps {
    fe: ExternalFinancialEntity;
    form: Record<string, any>;
    fields: FieldConfig[];
    linkedFields: string[];
    onFieldChange: (name: string, value: any) => void;
}

const WIDE_INPUT_WIDTH = 40;
const NARROW_INPUT_WIDTH = 20;

const FinancialEntityDetailInfoSection: FC<
    FinancialEntityDetailInfoSectionProps
> = ({ fe, form, fields, linkedFields, onFieldChange }) => {
    const { track } = useTracking();
    const [invertAmounts] = useMutation(InvertTransactionEntryAmountsDocument);
    const { showSuccessToast, showErrorToast } = useToasts();

    const sectionTitle = getConfig(fe.type).detailView.infoSectionTitle;
    const primaryField = getConfig(fe.type).detailView.primaryField;

    // The same logic we use in getInitialState for FinancialEntityDetailView.tsx
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const primaryFieldValue = fe.latestSnapshot?.[primaryField.name];
    const formattedPrimaryFieldValue = formatValue(
        primaryFieldValue,
        primaryField
    );

    // TODO: We should consider implementing unified approach where we fully
    // support conditional fields in our internal form/fields definitions
    const hideFields = form['isPaidOffMonthly'] === 'true';

    const feCombinedTypeChangeOptions = getFECombinedTypeOptions(
        fe.type,
        fe.ownershipType
    );

    return (
        <EntityViewGridSection title={sectionTitle}>
            <FlexCol px={8} py={6} gap={4} bg="gray.50">
                {fe.institution?.name && fe.institution.logo && (
                    <FlexRow align="center">
                        <FlexRow flex={1}>
                            <Text>Institution</Text>
                        </FlexRow>
                        <FlexRow flex={1} justifyContent="flex-end">
                            <InstitutionLogo
                                financialEntity={{
                                    institutionLogo: fe.institution.logo,
                                    name: getName(fe),
                                }}
                                mr={2}
                            />
                            <Text color="gray.500">{fe.institution.name}</Text>
                        </FlexRow>
                    </FlexRow>
                )}
                <OwnershipTypeRow fe={fe} />

                <FlexRow align="center" justifyContent="space-between">
                    <Text fontSize="md">Account type</Text>
                    {feCombinedTypeChangeOptions.length > 0 && fe.isMine ? (
                        <FlexRow fontWeight="medium">
                            <InputField
                                name="accountType"
                                label="Account type"
                                type="fancySelect"
                                fancySelectOptions={{
                                    isClearable: false,
                                }}
                                options={feCombinedTypeChangeOptions}
                                value={getFECombinedType(fe.info)}
                                onChange={({ value }) =>
                                    showFinancialEntityTypeChangeModal({
                                        fe,
                                        newFECombinedType: value,
                                    })
                                }
                                labelProps={{
                                    display: 'none',
                                }}
                                inputProps={{
                                    variant: 'outline',
                                    size: 'sm',
                                }}
                                isReadOnly={!fe.isMine}
                                bg="white"
                                w={WIDE_INPUT_WIDTH}
                            />
                        </FlexRow>
                    ) : configByFECombinedType[getFECombinedType(fe.info)]
                          ?.label ? (
                        <Text color="gray.500">
                            {
                                configByFECombinedType[
                                    getFECombinedType(fe.info)
                                ]?.label
                            }
                        </Text>
                    ) : (
                        <Text color="gray.500">
                            {getFEBaseConfig(fe.type).title}
                        </Text>
                    )}
                </FlexRow>

                {!hideFields &&
                    fields.map((field) => (
                        <FlexRow
                            key={field.name}
                            align="center"
                            justifyContent="space-between"
                        >
                            <Text fontSize="md">{field.label}</Text>
                            {linkedFields.includes(field.name) || !fe.isMine ? (
                                <Text fontSize="md" color="gray.500" py={2}>
                                    {formatValue(form[field.name], field)}
                                </Text>
                            ) : (
                                <FlexRow>
                                    <InputField
                                        {...field}
                                        value={form[field.name]}
                                        onChange={(value) =>
                                            onFieldChange(field.name, value)
                                        }
                                        labelProps={{
                                            display: 'none',
                                        }}
                                        inputProps={{
                                            width:
                                                field.type === 'text' ||
                                                field.type === 'currency'
                                                    ? WIDE_INPUT_WIDTH
                                                    : NARROW_INPUT_WIDTH,
                                            variant: 'outline',
                                            textAlign: 'right',
                                            size: 'sm',
                                            fontSize: 'sm',
                                            fontWeight: 500,
                                        }}
                                    />
                                </FlexRow>
                            )}
                        </FlexRow>
                    ))}
                <FlexCol gap={8} py={2}>
                    <FlexRow justifyContent="space-between">
                        <Text>Connection</Text>
                        <ConnectionStatus linkStatus={fe.linkStatus} />
                    </FlexRow>
                    <FlexRow justifyContent="space-between">
                        <Text>Last updated balance</Text>
                        <Text color="gray.500">
                            {formattedPrimaryFieldValue}
                        </Text>
                    </FlexRow>
                    <FlexRow justifyContent="space-between">
                        <Text>Balance last updated</Text>
                        {isLatestBalanceAccurate(fe) ? (
                            <Text color="gray.500">
                                {getLastUpdatedBalanceDate(fe).fromNow()}
                            </Text>
                        ) : (
                            <FlexRow>
                                <Text color="gray.500">--</Text>
                                <UnknownBalanceDateInfoButton
                                    balanceDate={getLastUpdatedBalanceDate(
                                        fe
                                    ).fromNow()}
                                />
                            </FlexRow>
                        )}
                    </FlexRow>
                    <FlexRow justifyContent="space-between">
                        <Text>Transactions last updated</Text>
                        {}
                        <Text color="gray.500">
                            {getTransactionsLastUpdatedAt(fe)?.fromNow() ??
                                'Never'}
                        </Text>
                    </FlexRow>
                    {fe.mask && (
                        <FlexRow justifyContent="space-between">
                            <Text>Account #</Text>
                            <Text color="gray.500">....{fe.mask}</Text>
                        </FlexRow>
                    )}
                    <FlexRow justifyContent="space-between" align="center">
                        <Text>Transaction signs</Text>
                        <InputField
                            name="invertAmount"
                            label="Invert amount"
                            type="fancySelect"
                            fancySelectOptions={{
                                isClearable: false,
                            }}
                            options={[
                                {
                                    label: 'Original',
                                    value: 'original',
                                },
                                {
                                    label: 'Reversed',
                                    value: 'reversed',
                                },
                            ]}
                            value={
                                fe.invertTransactionEntryAmounts
                                    ? 'reversed'
                                    : 'original'
                            }
                            onChange={() => {
                                track(
                                    'Invert Transaction Signs Button Clicked'
                                );

                                showConfirmModal({
                                    title: 'Reverse transaction signs',
                                    description: (
                                        <FlexCol gap={4}>
                                            <Paragraph color="textMuted">
                                                Some institutions provide
                                                transaction data with the wrong
                                                signs (e.g., “spending” might
                                                appear as positive at some
                                                institutions and negative at
                                                others).
                                            </Paragraph>
                                            <Paragraph color="textMuted">
                                                Click ‘Confirm’ to reverse the
                                                signs on your transactions. You
                                                can undo this at anytime.
                                            </Paragraph>
                                        </FlexCol>
                                    ),
                                    buttonText: 'Confirm',
                                    onConfirm: async () => {
                                        try {
                                            await invertAmounts({
                                                variables: {
                                                    financialEntityId: fe.id,
                                                },
                                            });

                                            showSuccessToast(
                                                TOASTS.invertAmountsSuccess
                                            );
                                        } catch (e) {
                                            logError(e);
                                            showErrorToast(
                                                TOASTS.unexpectedError
                                            );
                                        }
                                    },
                                    onClickPrimaryEvent:
                                        'Confirm Invert Transaction Signs Button Clicked',
                                    onClickSecondaryEvent:
                                        'Cancel Invert Transaction Signs Button Clicked',
                                });
                            }}
                            labelProps={{
                                display: 'none',
                            }}
                            inputProps={{
                                variant: 'outline',
                                size: 'sm',
                            }}
                            bg="white"
                            w={WIDE_INPUT_WIDTH}
                        />
                    </FlexRow>
                </FlexCol>

                {canLink(fe) && <ConnectYourAccount fe={fe} />}
            </FlexCol>
        </EntityViewGridSection>
    );
};

const OwnershipTypeRow = ({ fe }: { fe: ExternalFinancialEntity }) => {
    const { activePartnershipId } = useCurrentUser();

    // If user does not have a partner, hide ownership row
    // because we don't support selecting Individual/Joint.
    if (!activePartnershipId) return <></>;

    const ownershipTypeOptionValues = getOwnershipTypeOptionValues({
        feType: fe.type,
        feJointOwnerPartnershipId: fe.jointOwnerPartnershipId,
        feIsMine: fe.isMine,
        activePartnershipId,
    });
    const ownershipTypeOptions = ownershipTypeOptionValues.map((value) => {
        return {
            value,
            label: <OwnershipType value={value} />,
        };
    });

    return (
        <FlexRow align="center" justifyContent="space-between">
            <Text fontSize="md">Ownership</Text>
            <FlexRow>
                {ownershipTypeOptionValues.length === 1 ? (
                    <Text color="gray.500" py={2}>
                        <OwnershipType value={fe.ownershipType} />
                    </Text>
                ) : (
                    <InputField
                        type="fancySelect"
                        fancySelectOptions={{
                            isClearable: false,
                        }}
                        value={fe.ownershipType}
                        onChange={({ value }) => {
                            if (value === fe.ownershipType) return; // no change

                            if (value === 'JOINT') {
                                // Individual -> Joint
                                showAccountLinkingConfirmationModal({
                                    financialEntityIds: [fe.id],
                                    startWithJointOwnerSubstep: true,
                                    closeOnEsc: true,
                                    showCloseButton: true,
                                    closeOnOverlayClick: true,
                                });
                            } else {
                                // Joint -> Individual
                                showFinancialEntityJointToIndividualModal({
                                    fe,
                                });
                            }
                        }}
                        name="ownershipType"
                        options={[
                            {
                                label: 'WHO OWNS THIS ACCOUNT?',
                                options: ownershipTypeOptions,
                            },
                        ]}
                        inputProps={{
                            variant: 'outline',
                            size: 'sm',
                        }}
                        fontWeight="medium"
                        bg="white"
                        w={WIDE_INPUT_WIDTH}
                    />
                )}
            </FlexRow>
        </FlexRow>
    );
};

const ConnectionStatus: FC<{ linkStatus: LinkStatus }> = ({ linkStatus }) => {
    switch (linkStatus) {
        case 'MANUAL':
            return <Text color="gray.500">Manual</Text>;
        case 'PLAID_DISCONNECTED':
        case 'PLAID_ERROR':
        case 'MONEY_KIT_DISCONNECTED':
        case 'MONEY_KIT_ERROR':
            return (
                <FlexRow alignItems="center" gap={2}>
                    <Text color="gray.500">Disconnected</Text>
                    <LinkBroken01 size="sm" color="black" />
                </FlexRow>
            );
        case 'MONEY_KIT_CONNECTED':
        case 'PLAID_CONNECTED':
            return (
                <FlexRow alignItems="center" gap={2}>
                    <Text color="gray.500">Connected</Text>
                    <Link03 size="sm" color="gray.500" />
                </FlexRow>
            );
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = linkStatus;
            return <></>;
        }
    }
};

const ConnectYourAccount: FC<{
    fe: ExternalFinancialEntity;
}> = ({ fe }) => {
    const { open } = usePlaid({
        financialEntity: {
            id: fe.id,
            type: fe.type,
            linkStatus: fe.linkStatus,
        },
    });
    return (
        <FlexCol mt={8}>
            <Paragraph color="textMuted" fontSize="sm" pb={5}>
                {`Plenty uses bank-level AES-256 encryption to keep your data safe. We'll never sell your data.`}
            </Paragraph>
            <FlexRow>
                <ButtonWithIcon
                    iconLeft={<Link03 size="sm" mr={1} />}
                    variant="link"
                    size="sm"
                    tabIndex={99}
                    onClick={open}
                    onClickEvent="Connect Account Button Clicked"
                    onClickEventParams={{ location: 'Financial Entity Detail' }}
                >
                    Connect your account
                </ButtonWithIcon>
            </FlexRow>
        </FlexCol>
    );
};

export default FinancialEntityDetailInfoSection;
